<template>
  <div>
    <ValidationObserver ref="observer" tag="fieldset" v-slot="{ handleSubmit, invalid }">
      <b-form @submit.prevent="handleSubmit(onSubmit)">
      <b-row v-if="payoutData.status !== payoutStatuses.COMPLETED">
        <b-col>
          <distribution-form-group
            :label="$t('transaction_batch_id')"
            :description="$t('batch_id_desc')"
            label-cols="2"
            vid="transactionBatchId"
            ref="transactionBatchId"
            v-slot="{ state }"
            hide-feedback
          >
            <b-col v-if="payoutMode === payoutModes.VIEW" class="view-values" cols="10"> {{ payoutData.transactionBatchId }} </b-col>
            <b-form-input v-else v-model="payoutData.transactionBatchId" :state="state" :disabled="true"/>
          </distribution-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('payout_id')"
            :description="$t('payout_id_desc')"
            label-cols="2"
            vid="payoutId"
            ref="payoutId"
            v-slot="{ state }"
            hide-feedback
          >
            <b-col v-if="payoutMode === payoutModes.VIEW" class="view-values" cols="10"> {{ payoutData.payoutId }} </b-col>
            <b-form-input v-else v-model="payoutData.payoutId" :state="state" :disabled="true"/>
          </distribution-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('created_by') | capitalize"
            :description="$t('created_by_desc')"
            label-cols="2"
            vid="createdBy"
            ref="createdBy"
            v-slot="{ state }"
            hide-feedback
          >
            <b-col v-if="payoutMode === payoutModes.VIEW" class="view-values" cols="10"> {{ payoutData.createdByName }} </b-col>
            <b-form-input v-else v-model="payoutData.createdByName" :state="state" :disabled="true"/>
          </distribution-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-2"> 
        <b-col>
          <distribution-form-group
            :label="$t('managed_by') | capitalize"
            label-cols="2"
            rules="required"
            vid="managedBy"
            ref="managedBy"
            v-slot="{ state }"
            :hide-feedback="payoutMode === payoutModes.VIEW"
          >
          <b-col v-if="payoutMode === payoutModes.VIEW" class="view-values" cols="10"> {{ payoutData.managedBy }} </b-col>
            <b-input-group v-else class="select-search-append">
              <template v-slot:append>
                <select-search
                  class="select-search-input"
                  :list-endpoint="$api.users.usersSearch"
                  label=""
                  :placeholder="$t('search_user') | capitalize"
                  search-name="search"
                  exclude-name="id"
                  text-field="name"
                  value-field="email"
                  v-model="selectedUser"
                  ref="selectUser"
                  :show-value="false"
                  :searchValue="payoutData.managedBy"
                />
              </template>
              <b-form-input hidden=true required v-model="payoutData.managedBy" :state="state"/>
            </b-input-group>
            <fa-icon v-if="payoutMode === payoutModes.EDIT" :icon="['fa', 'pen']" class="ml-1 edit-svg"/>
          </distribution-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('description') | capitalize"
            label-cols="2"
            rules="required"
            vid="description"
            ref="description"
            v-slot="{ state }"
            :hide-feedback="payoutMode === payoutModes.VIEW"
          >
            <b-col v-if="payoutMode === payoutModes.VIEW" class="view-values" cols="10"> {{ payoutData.description }} </b-col>
            <b-form-input v-else required v-model="payoutData.description" :state="state"/>
            <fa-icon v-if="payoutMode === payoutModes.EDIT" :icon="['fa', 'pen']" class="ml-1 edit-svg"/>
          </distribution-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('estimated_completion_date') | capitalize"
            label-cols="2"
            vid="estimatedCompletionDate"
            ref="estimatedCompletionDate"
            rules="required"
            v-slot="{ state }"
            :hide-feedback="payoutMode === payoutModes.VIEW"
            >
            <b-col v-if="payoutMode === payoutModes.VIEW" class="view-values" cols="10"> {{ payoutData.estimatedCompletionDate }} </b-col>
            <date-picker
              v-else
              :input-attr="{required: true}"
              type="date"
              v-model="payoutData.estimatedCompletionDate"
              :class="{invalid: state === false}"
              :format="$config.ISO_DATE_FORMAT"
              :value-type="$config.ISO_DATE_FORMAT"
            />
            <fa-icon v-if="payoutMode === payoutModes.EDIT" :icon="['fa', 'pen']" class="ml-1 edit-svg"/>
          </distribution-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('actual_completion_date') | capitalize"
            :description="$t('actual_completion_date_desc')"
            label-cols="2"
            vid="actualCompletionDate"
            ref="actualCompletionDate"
            v-slot="{ state }"
            hide-feedback
            >
            <b-col v-if="payoutMode === payoutModes.VIEW" class="view-values" cols="10"> {{ payoutData.completionDate }} </b-col>
            <date-picker
              v-else
              type="date"
              v-model="payoutData.completionDate"
              :class="{invalid: state === false}"
              :format="$config.DATE_FORMAT"
              :disabled="true"
            />
          </distribution-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('tariffs_in_scope') | capitalize"
            :description="$t('tariffs_in_scope_desc')"
            label-cols="2"
            vid="distributionsCount"
            ref="distributionsCount"
            v-slot="{ state }"
            hide-feedback
          >
            <b-col v-if="payoutMode === payoutModes.VIEW" class="view-values" cols="10"> {{ payoutData.distributionsCount }} </b-col>
            <b-form-input v-else v-model="payoutData.distributionsCount" :state="state" :disabled="true"/>
          </distribution-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('tariffs_included') | capitalize"
            :description="$t('tariffs_included_desc')"
            label-cols="2"
            vid="tariffsIncludedInPayout"
            ref="tariffsIncludedInPayout"
            v-slot="{ state }"
            hide-feedback
          >
            <b-col v-if="payoutMode === payoutModes.VIEW" class="view-values" cols="10">
              <NamesListModal class-names="mb-2" :values="mapTariffs(payoutData.distributionsMap)" modalTitle="tariff periods"/>
            </b-col>
            <b-form-input v-else v-model="tariffString" :state="state" :disabled="true"/>
          </distribution-form-group>
        </b-col>
      </b-row>
      <div class="fixed-footer">
        <div class="d-flex justify-content-end">
          <b-button
          class="ml-3 mr-3 btn-outline-red"
          type="submit"
          :disabled="invalid"
          v-if="payoutMode === payoutModes.CREATE"
          >
            {{ $t("create") | capitalize }}
          </b-button>

          <b-button v-if="payoutMode === payoutModes.VIEW && canEdit" class="btn-outline-red" @click="editForm">{{ $t("edit_details") | capitalize }}</b-button>
          <b-button v-if="payoutMode === payoutModes.EDIT" class="btn-outline-dark mr-3" @click="cancel">{{ $t("cancel") | capitalize }}</b-button>
          <b-button v-if="payoutMode === payoutModes.EDIT" class="btn-outline-red" :disabled="invalid" @click="saveForm">{{ $t("save_changes") | capitalize }}</b-button>
          
          <b-button 
          class="btn-outline-red ml-3 mr-3"
          v-if="payoutMode !== payoutModes.CREATE && canEdit"
          @click="nextTab"
          >
            {{ $t("modify_scope") | capitalize }}
          </b-button>
        </div>
      </div>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script>

import { mapActions, mapState } from "vuex"
import { payoutModes, payoutStatuses } from "@/constants"
import DistributionFormGroup from "@/components/DistributionFormGroup"
import NamesListModal from "@/pages/Repertoire/Recordings/NamesListModal"
import { ValidationObserver } from "vee-validate"
import { capitalize } from "lodash"

export default {
  name: "DetailsSection",
  components: { DistributionFormGroup, ValidationObserver, NamesListModal },
  props: {
    payoutData: {
      type: Object,
    },
    payoutMode: String,
    canEdit: Boolean,
  },
  data () {
    return {
      selectedUser: null,
      capitalize,
      payoutModes,
      tariffString: "",
      payoutStatuses,
    }
  },
  computed : {
    ...mapState("user", ["user"]),
  },
  methods: {
    ...mapActions("alert", ["success", "error"]),
    onSubmit () {      
      let data = {
        estimatedCompletionDate: this.payoutData.estimatedCompletionDate,
        description: this.payoutData.description,
        managedBy: this.payoutData.managedBy,
        createdBy: this.user.uuid,
        status: this.payoutData.status,
      }
      this.$api.payouts.createPayout(data).then((response) => {
        let payoutId = response.data.id
        return this.$emit("get-data", payoutId)
      }).then(() => {
        this.success(capitalize(this.$t("created_successfully")))
        this.$emit("change-payout-mode", payoutModes.VIEW)
      }).catch(error => {
        const { data, status } = error.response
        if (status === 400) {
          this.error(capitalize(this.$t("form_contains_errors")))
          this.$refs.scopeForm.setErrors(data)
        }
      }) 
    },
    editForm () {
      this.$emit("change-payout-mode", payoutModes.EDIT)
    },
    saveForm () {
      let data = {
        estimatedCompletionDate: this.payoutData.estimatedCompletionDate,
        description: this.payoutData.description,
        managedBy: this.payoutData.managedBy,
        createdBy: this.user.uuid,
        status: this.payoutData.status,
      }
      this.$api.payouts.updatePayout(this.payoutData.id, data).then((response) => {
        this.$emit("update-payout-data", response.data["results"])
        this.$emit("change-payout-mode", payoutModes.VIEW)
        this.success(capitalize(this.$t("saved_changes")))
      }).catch(error => {
        const { data, status } = error.response
        if (status === 400) {
          this.error(capitalize(this.$t("form_contains_errors")))
          this.$refs.scopeForm.setErrors(data)
        }
      }) 
    },
    cancel () {
      this.$emit("reset")
    },
    nextTab () {
      this.$parent.bvTabs.nextTab()
    },
    mapTariffs (tariffObj) {
      if (tariffObj) {
        this.tariffString = Object.values(tariffObj).map(item => item.description).join(", ")
        return Object.values(tariffObj).map(item => item.description)
      }
      return []
    }
  },
  watch : {
    selectedUser (newVal) {
      if (newVal && typeof newVal === "object") {
        this.payoutData.managedBy = newVal.value
      } else {
        this.payoutData.managedBy = null
      }
    }
  },
}
</script>

<style lang="css" scoped>
.select-search-append {
  width: stretch;
  .input-group-append {
    width: stretch;
    .select-search-input {
      width: stretch;
    }
  }
  ::v-deep .form-control[readonly] {
    background-color: transparent;
  }
}
.edit-svg {
  height: 32px;
}
</style>